import { useCallback } from "react";
import {
  ab2str,
  getMobileOperatingSystem,
  getModuleNameFromPath,
  publicKeyCredentialCreationOptions,
  publicKeyCredentialRequestOptions,
  str2ab,
} from "../utils/helper";
import {
  T_BiometricCapture,
  T_BiometricVerify,
} from "../globalTypes/GlobalTypes";
import { useAppSelector } from "../redux-store/CustomReduxHooks";
import { ModuleNameKey, pathToModuleName } from "../utils/constants";
import { useSaveUIException } from "./useSaveUIException";
interface BiometricResponse {
  status: string;
  data: string;
}

declare const window: any;

export const useBiometric = (purpose: "capture" | "verify") => {
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const { triggerSaveUIException } = useSaveUIException();

  const registerBiometric = useCallback((): Promise<T_BiometricCapture> => {
    return new Promise(async (resolve, reject) => {
      if (!navigator.credentials) {
        return alert(
          "Your browser does not support the Web Authentication API",
        );
      }
      let credentials: any = "";
      let bioToken: any = "";
      try {
        if (getMobileOperatingSystem() === "iOS") {
          calliOSBiometric({ type: "biometric", isEnable: true })
            .then((response: unknown) => {
              const biometricResponse = response as BiometricResponse;
              console.log("Response from native code:", biometricResponse);

              if (biometricResponse.status === "success") {
                bioToken = biometricResponse.data;
                console.log("Successfully biometric setup done", bioToken);
                return resolve({
                  isBiometricEnable: true,
                  biometricToken: bioToken,
                });
              }
            })
            .catch((error) => {
              const currentPath = window?.location?.pathname;
              triggerSaveUIException({
                moduleName: getModuleNameFromPath(currentPath),
                pageName: currentPath || "",
                statusCode: 500,
                uiDisplayMessage: `fileName(useBiometric.ts) and functionName(calliOSBiometric----> condition(getMobileOperatingSystem() === "iOS")) `,
                errorMessage: {
                  stk: error.stack || "No stack available",
                  msg: error.message || "Unknown error occurred",
                },
              });
            });
        } else if (csAppSlice.NativeDeviceInfo?.systemName === "Android") {
          callAndroidBiometric(purpose)
            .then((bioToken) => {
              console.log("Bio Info", bioToken);
              return resolve({
                isBiometricEnable: true,
                biometricToken: bioToken as string,
              });
            })
            .catch((error) => {
              const currentPath = window?.location?.pathname;
              triggerSaveUIException({
                moduleName: getModuleNameFromPath(currentPath),
                pageName: currentPath || "",
                statusCode: 500,
                uiDisplayMessage: `fileName(useBiometric.ts) and functionName(calliOSBiometric -------> condition (csAppSlice.NativeDeviceInfo?.systemName === 'Android')) `,
                errorMessage: {
                  stk: error.stack || "No stack available",
                  msg: error.message || "Unknown error occurred",
                },
              });
            });
        } else {
          credentials = await navigator.credentials.create(
            publicKeyCredentialCreationOptions,
          );
          bioToken = ab2str(credentials?.rawId);
          console.log(
            "Successfully biometric setup done",
            ab2str(credentials?.rawId),
          );
          return resolve({ isBiometricEnable: true, biometricToken: bioToken });
        }
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  }, [csAppSlice.NativeDeviceInfo?.systemName, triggerSaveUIException]);

  const verifyPasskey = useCallback(
    (biometricToken: string): Promise<T_BiometricVerify> => {
      return new Promise(async (resolve, reject) => {
        try {
          if (getMobileOperatingSystem() === "iOS") {
            calliOSBiometric({ type: "biometric", isEnable: true })
              .then((response: unknown) => {
                const biometricResponse = response as BiometricResponse;
                console.log("Response from native code:", biometricResponse);

                if (biometricResponse.status === "success") {
                  // Handle the response as needed
                  resolve({
                    isBiometricEnable: true,
                    isBiometricTokenVerified: true,
                  });
                }
              })
              .catch((error) => {
                const currentPath = window?.location?.pathname;
                triggerSaveUIException({
                  moduleName: getModuleNameFromPath(currentPath),
                  pageName: currentPath || "",
                  statusCode: 500,
                  uiDisplayMessage: `fileName(useBiometric.ts) and functionName(calliOSBiometric ------->condition(getMobileOperatingSystem() === "iOS")) `,
                  errorMessage: {
                    stk: error.stack || "No stack available",
                    msg: error.message || "Unknown error occurred",
                  },
                });
              });
          } else if (csAppSlice.NativeDeviceInfo?.systemName === "Android") {
            callAndroidBiometric(purpose)
              .then((bioToken) => {
                console.log("Bio Info", bioToken);
                resolve({
                  isBiometricEnable: true,
                  isBiometricTokenVerified: true,
                });
              })
              .catch((error) => {
                const currentPath = window?.location?.pathname;
                triggerSaveUIException({
                  moduleName: getModuleNameFromPath(currentPath),
                  pageName: currentPath || "",
                  statusCode: 500,
                  uiDisplayMessage: `fileName(useBiometric.ts) and functionName(callAndroidBiometric ------> condition(csAppSlice.NativeDeviceInfo?.systemName === 'Android')) `,
                  errorMessage: {
                    stk: error.stack || "No stack available",
                    msg: error.message || "Unknown error occurred",
                  },
                });
              });
          } else {
            const rawId = str2ab(biometricToken ? biometricToken : "");
            const creds = publicKeyCredentialRequestOptions(rawId);
            const credentials = await navigator.credentials.get(creds);
            resolve({
              isBiometricEnable: true,
              isBiometricTokenVerified: true,
            });
          }
          //   appDispatch({
          //     type: SIGNIN,
          //     payload: { token: credentials?.id },
          //   });
        } catch (err) {
          console.log(err);
          reject(err);
        }
      });
    },
    [csAppSlice.NativeDeviceInfo?.systemName, triggerSaveUIException],
  );

  const initiateBiometricScan = useCallback(
    (token: string) => {
      if (purpose === "capture") {
        return registerBiometric();
      } else {
        return verifyPasskey(token);
      }
    },
    [purpose, registerBiometric, verifyPasskey],
  );
  return initiateBiometricScan;
};

function calliOSBiometric(message: any) {
  console.log("calliOSBiometric calling");
  return new Promise((resolve, reject) => {
    window.handleResponse = function (response: any) {
      resolve(response);
      delete window.handleResponse;
    };

    const webkit = (window as any).webkit;

    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.customHandler
    ) {
      window.webkit.messageHandlers.customHandler.postMessage(message);
    } else {
      reject(new Error("Native messaging not available."));
    }
  }).finally(() => {
    delete window.handleResponse;
  });
}

const callAndroidBiometric = (purpose: string) => {
  const message = { action: "LAUNCH_BIOMETRIC", purpose: purpose };
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    console.log(
      "Sending event to TWA React Native Android " + JSON.stringify(message),
    );
  }
  return new Promise((resolve) => {
    const messageHandler = (event: CustomEvent) => {
      console.log("BIO Info received", event.detail);
      resolve(event.detail);
      window.removeEventListener("biometric_info", messageHandler); // Clean up the listener
    };
    window.addEventListener("biometric_info", messageHandler);
  });
};
